import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Search, User, MapPin, Trash2, ExternalLink,
  Calendar, Link2, Edit2, X, Check, Clock, Users
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: "New", color: "bg-gray-100 text-gray-800" },
  { value: "Reviewing", color: "bg-blue-100 text-blue-800" },
  { value: "Contacted", color: "bg-yellow-100 text-yellow-800" },
  { value: "Audition Scheduled", color: "bg-purple-100 text-purple-800" },
  { value: "Finalized", color: "bg-green-100 text-green-800" },
  { value: "Rejected", color: "bg-red-100 text-red-800" }
];

const getStatusColor = (status) => {
  const found = STATUS_OPTIONS.find(s => s.value === status);
  return found ? found.color : "bg-gray-100 text-gray-800";
};

export default function TalentBoard() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editModal, setEditModal] = useState(null); // { entry, field: 'notes' | 'meeting' }

  const fetchEntries = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const url = search 
        ? `${API_URL}/api/talentboard/list?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/talentboard/list`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error("Error fetching TalentBoard:", error);
      toast.error("Failed to load TalentBoard");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleStatusChange = async (entryId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/talentboard/${entryId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setEntries(prev => prev.map(e => 
          e.id === entryId ? { ...e, status: newStatus } : e
        ));
        toast.success("Status updated");
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleRemove = async (entryId, name) => {
    if (!window.confirm(`Remove ${name} from TalentBoard?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/talentboard/${entryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setEntries(prev => prev.filter(e => e.id !== entryId));
        toast.success("Removed from TalentBoard");
      } else {
        toast.error("Failed to remove");
      }
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    
    try {
      const token = localStorage.getItem("token");
      const updateData = {};
      
      if (editModal.field === "notes") {
        updateData.notes = editModal.notes || "";
      } else if (editModal.field === "meeting") {
        updateData.meeting_link = editModal.meeting_link || "";
        updateData.meeting_date = editModal.meeting_date || "";
      }

      const response = await fetch(`${API_URL}/api/talentboard/${editModal.entry.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setEntries(prev => prev.map(e => 
          e.id === editModal.entry.id ? { ...e, ...updateData } : e
        ));
        toast.success(editModal.field === "notes" ? "Notes saved" : "Meeting details saved");
        setEditModal(null);
      } else {
        toast.error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/enterprise")} data-testid="talentboard-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-6 h-6 text-violet-600" />
                  TalentBoard
                </h1>
                <p className="text-sm text-gray-500">{entries.length} profiles saved</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="talentboard-search"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {entries.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No profiles in TalentBoard</h3>
              <p className="text-gray-500 mb-4">
                Browse profiles and click "Add to TalentBoard" to start managing your shortlist.
              </p>
              <Button onClick={() => navigate("/browse")} className="bg-violet-600 hover:bg-violet-700">
                Browse Profiles
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profile</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role & Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meeting</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {entries.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50">
                          {/* Profile */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                {entry.profile_photo ? (
                                  <img src={entry.profile_photo} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <User className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <a 
                                  href={`/p/${entry.profile_slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-gray-900 hover:text-violet-600 flex items-center gap-1"
                                >
                                  {entry.profile_name || "Unknown"}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            </div>
                          </td>
                          
                          {/* Role & Location */}
                          <td className="px-4 py-3">
                            <div className="text-sm">
                              <Badge variant="secondary" className="mb-1">
                                {entry.profile_type === "trainer" ? "Trainer" : "Talent"}
                              </Badge>
                              <div className="text-gray-500">{entry.profile_category || "-"}</div>
                              {entry.profile_location && (
                                <div className="text-gray-400 flex items-center gap-1 text-xs">
                                  <MapPin className="w-3 h-3" /> {entry.profile_location}
                                </div>
                              )}
                            </div>
                          </td>
                          
                          {/* Status */}
                          <td className="px-4 py-3">
                            <select
                              value={entry.status}
                              onChange={(e) => handleStatusChange(entry.id, e.target.value)}
                              className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${getStatusColor(entry.status)}`}
                              data-testid={`status-select-${entry.id}`}
                            >
                              {STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.value}</option>
                              ))}
                            </select>
                          </td>
                          
                          {/* Notes */}
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setEditModal({ entry, field: "notes", notes: entry.notes || "" })}
                              className="text-sm text-gray-600 hover:text-violet-600 flex items-center gap-1 max-w-[150px] truncate"
                              data-testid={`notes-btn-${entry.id}`}
                            >
                              {entry.notes ? (
                                <span className="truncate">{entry.notes}</span>
                              ) : (
                                <span className="text-gray-400 italic">Add notes...</span>
                              )}
                              <Edit2 className="w-3 h-3 flex-shrink-0" />
                            </button>
                          </td>
                          
                          {/* Meeting - De-emphasized */}
                          <td className="px-4 py-3">
                            <div className="text-xs text-gray-400">
                              {entry.meeting_date ? (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(entry.meeting_date)}
                                </span>
                              ) : (
                                <span className="italic">Use Projects for meetings</span>
                              )}
                            </div>
                          </td>
                          
                          {/* Added Date */}
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {formatDate(entry.created_at)}
                          </td>
                          
                          {/* Actions */}
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemove(entry.id, entry.profile_name)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-btn-${entry.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {entries.map((entry) => (
                <Card key={entry.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-14 h-14 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
                        {entry.profile_photo ? (
                          <img src={entry.profile_photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <a 
                          href={`/p/${entry.profile_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-gray-900 hover:text-violet-600 flex items-center gap-1"
                        >
                          {entry.profile_name || "Unknown"}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {entry.profile_type === "trainer" ? "Trainer" : "Talent"}
                          </Badge>
                          <span className="text-xs text-gray-500">{entry.profile_category}</span>
                        </div>
                        {entry.profile_location && (
                          <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {entry.profile_location}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(entry.id, entry.profile_name)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Status */}
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-xs text-gray-500">Status</Label>
                      <select
                        value={entry.status}
                        onChange={(e) => handleStatusChange(entry.id, e.target.value)}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border-0 ${getStatusColor(entry.status)}`}
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.value}</option>
                        ))}
                      </select>
                    </div>

                    {/* Notes & Meeting */}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setEditModal({ entry, field: "notes", notes: entry.notes || "" })}
                        className="text-left p-2 bg-gray-50 rounded-lg hover:bg-gray-100"
                      >
                        <div className="text-xs text-gray-500 mb-1">Notes</div>
                        <div className="text-sm truncate">
                          {entry.notes || <span className="text-gray-400 italic">Add...</span>}
                        </div>
                      </button>
                      <button
                        onClick={() => setEditModal({ 
                          entry, 
                          field: "meeting", 
                          meeting_link: entry.meeting_link || "",
                          meeting_date: entry.meeting_date || ""
                        })}
                        className="text-left p-2 bg-gray-50 rounded-lg hover:bg-gray-100"
                      >
                        <div className="text-xs text-gray-500 mb-1">Meeting</div>
                        <div className="text-sm">
                          {entry.meeting_date ? formatDate(entry.meeting_date) : <span className="text-gray-400 italic">Set...</span>}
                        </div>
                      </button>
                    </div>

                    {/* Footer */}
                    <div className="text-xs text-gray-400 mt-3 pt-3 border-t flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Added {formatDate(entry.created_at)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">
                {editModal.field === "notes" ? "Edit Notes" : "Edit Meeting Details"}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditModal(null)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {editModal.field === "notes" ? (
                <div className="space-y-2">
                  <Label>Private Notes</Label>
                  <Textarea
                    value={editModal.notes}
                    onChange={(e) => setEditModal(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add your private notes about this profile..."
                    rows={4}
                  />
                  <p className="text-xs text-gray-500">Only visible to your enterprise account</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={editModal.meeting_date?.split('T')[0] || ''}
                        onChange={(e) => {
                          const time = editModal.meeting_time || '10:00';
                          setEditModal(prev => ({ 
                            ...prev, 
                            meeting_date: e.target.value ? `${e.target.value}T${time}` : ''
                          }));
                        }}
                        data-testid="meeting-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={editModal.meeting_time || editModal.meeting_date?.split('T')[1]?.substring(0,5) || '10:00'}
                        onChange={(e) => {
                          const date = editModal.meeting_date?.split('T')[0] || new Date().toISOString().split('T')[0];
                          setEditModal(prev => ({ 
                            ...prev, 
                            meeting_date: `${date}T${e.target.value}`,
                            meeting_time: e.target.value
                          }));
                        }}
                        data-testid="meeting-time-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Meeting Link (optional)</Label>
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-gray-400" />
                      <Input
                        type="url"
                        value={editModal.meeting_link}
                        onChange={(e) => setEditModal(prev => ({ ...prev, meeting_link: e.target.value }))}
                        placeholder="https://meet.google.com/..."
                        data-testid="meeting-link-input"
                      />
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditModal(null)} data-testid="modal-cancel-btn">Cancel</Button>
                <Button onClick={handleSaveEdit} className="bg-violet-600 hover:bg-violet-700" data-testid="modal-save-btn">
                  <Check className="w-4 h-4 mr-2" /> Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
